import React from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Box, Grid, Typography } from "@mui/material"
import SummarySection from "src/v2/components/SummarySection/summary-section"

const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-")

const getEmptyText = (state, fallback) => state.error ?? fallback

const desktopSummaryOrder = {
    rating: 1,
    probabilityDefault: 2,
    financial: 3,
    event: 4,
    fact: 5,
}

const getDesktopSummaryOrder = (key) => desktopSummaryOrder[key] ?? 99

const getLatestFinancialPeriod = (dataByGroup) => {
    const periods = Object.values(dataByGroup ?? {})
        .flatMap((group) => group.records ?? [])
        .map((record) => record.DataReferencia)
        .filter(Boolean)
        .sort()

    const latestPeriod = periods.at(-1)
    const [year, month] = String(latestPeriod ?? "").split("-")

    return year && month ? `${month}/${year}` : "-"
}

const toFinancialSummaryState = (financial) => ({
    data: financial.status === "success" ? financial : null,
    loading: financial.status === "loading",
    error: financial.status === "error" ? financial.error : null,
})

const CompanySummarySections = ({ summaries, financial, onOpenDetails }) => {
    const sections = [
        {
            key: "rating",
            title: "Último Rating Devedor",
            state: summaries.rating,
            emptyText: "Esse devedor não possui rating.",
            fields: [
                { label: "DATA VALIDADE", value: formatDate(summaries.rating.data?.DataValidade) },
                { label: "FONTE", value: summaries.rating.data?.Fonte },
                { label: "RATING", value: summaries.rating.data?.Rating },
            ],
        },
        {
            key: "probabilityDefault",
            title: "Última Probabilidade de default",
            state: summaries.probabilityDefault,
            emptyText: "Não há probabilidade de default disponível.",
            fields: [
                { label: "DATA REFERÊNCIA", value: formatDate(summaries.probabilityDefault.data?.DataReferencia) },
                { label: "PD", value: summaries.probabilityDefault.data?.PD },
            ],
        },
        {
            key: "event",
            title: "Último Evento de Crédito Devedor",
            state: summaries.event,
            emptyText: "Esse devedor não possui eventos de crédito.",
            fields: [
                { label: "DATA REFERÊNCIA", value: formatDate(summaries.event.data?.DataReferencia) },
                { label: "SCORE EVENTO", value: summaries.event.data?.ScoreEvento },
                {
                    label: "EVENTO CRÉDITO",
                    value: summaries.event.data?.EventoCreditoDevedor,
                    gridProps: { xxxs: 4, sm: 12 },
                },
            ],
        },
        {
            key: "fact",
            title: "Último Fato Relevante",
            state: summaries.fact,
            emptyText: "Não há fato relevante disponível.",
            fields: [
                { label: "DATA REFERÊNCIA", value: formatDate(summaries.fact.data?.DataReferencia) },
                {
                    label: "FATO RELEVANTE",
                    value: summaries.fact.data?.Assunto ?? summaries.fact.data?.Resumo,
                    gridProps: { xxxs: 4, sm: 12 },
                    externalLink: summaries.fact.data?.Link,
                    externalLinkLabel: "Abrir documento do fato relevante",
                },
            ],
        },
        {
            key: "financial",
            title: "Indicadores Financeiros e Balanço Patrimonial",
            state: toFinancialSummaryState(financial),
            emptyText: "Não há indicadores financeiros e balanço patrimonial disponíveis para esta empresa.",
            fields: [
                { label: "ÚLTIMA REFERÊNCIA", value: getLatestFinancialPeriod(financial.dataByGroup) },
                { label: "GRUPOS DISPONÍVEIS", value: financial.availableGroups.join(" e ") },
            ],
        },
    ]

    return (
        <Box sx={{ maxWidth: { xxxs: "100%", md: "580px", lg: "1000px" } }}>
            <Grid container rowSpacing={3} columnSpacing={2}>
                {sections.map((section) => (
                    <Grid
                        key={section.key}
                        item
                        xxxs={12}
                        md={6}
                        lg={4}
                        sx={{
                            display: "flex",
                            order: { lg: getDesktopSummaryOrder(section.key) },
                        }}
                    >
                        <SummarySection
                            title={section.title}
                            fields={section.fields}
                            loading={section.state.loading}
                            empty={!section.state.loading && !section.state.data}
                            emptyText={getEmptyText(section.state, section.emptyText)}
                            onClick={() => onOpenDetails(section.key)}
                        />
                    </Grid>
                ))}
            </Grid>
            {Object.values(summaries).some((summary) => summary.error) && (
                <Typography role="status" sx={{ mt: 2, color: "common.white" }}>
                    Algumas informações não puderam ser carregadas. Abra a seção para tentar novamente.
                </Typography>
            )}
        </Box>
    )
}

CompanySummarySections.propTypes = {
    summaries: PropTypes.shape({
        rating: PropTypes.object.isRequired,
        event: PropTypes.object.isRequired,
        fact: PropTypes.object.isRequired,
        probabilityDefault: PropTypes.object.isRequired,
    }).isRequired,
    financial: PropTypes.shape({
        status: PropTypes.string.isRequired,
        error: PropTypes.string,
        availableGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
        dataByGroup: PropTypes.object.isRequired,
    }).isRequired,
    onOpenDetails: PropTypes.func.isRequired,
}

export default CompanySummarySections
